import Drawer from '@material-ui/core/Drawer';
import { generateJobOrderPresentation } from 'features/ai-generators';
import {
  CallVerbiages,
  JobOrderPresentationTypeSelector,
  JobOrderRoleplayScenarioSelector,
  PresentationMakerTemplate
} from 'features/ai-generators/components';
import { CALL_VERBIAGES_TYPES } from 'features/ai-generators/constants';
import { CreationTaskDrawer } from 'features/daily-plan';
import { TASK_TYPES } from 'features/daily-plan/constants';
import {
  ProfileAccountableBar,
  ProfileHeaderActions,
  ProfilePageLayout,
  ProfileSummary,
  ProfileTabs
} from 'features/inventory-profiles/components/shared';
import {
  JOB_ORDER_DRAWERS,
  JOB_ORDER_PROFILE_CONFIG,
  JOB_ORDER_TAB_IDS
} from 'features/inventory-profiles/constants';
import {
  useJobOrderProfile,
  useProfileAccountability,
  useProfileDrawer,
  useTabRefresh
} from 'features/inventory-profiles/hooks';
import strings from 'strings';
import { FPActionButton, When } from 'UI/components/atoms';
import { VerifyUrgencyDialog } from 'UI/components/molecules/VerifyUrgencyDialog';
import ReferenceRelease from 'UI/components/organisms/candidates/ReferenceRelease';
import { ClaimAccountabilityDrawer } from 'UI/components/organisms/inventoryProfiles/ClaimAccountability/ClaimAccountabilityDrawer';
import { handleEntityEditionCompleted } from 'UI/components/organisms/inventoryProfiles/utils';
import JobOrderEdit from 'UI/components/organisms/JobOrderEdit';
import { PlacementCreateManager } from 'UI/components/organisms/placements/PlacementCreateManager';
import ReassignRecruiterForm from 'UI/components/organisms/ReassignRecruiter';
import SendoutsForm from 'UI/components/organisms/sendouts/SendoutForm';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType, JobOrderEntity } from 'UI/constants/entityTypes';
import { SvgMakeAi } from 'UI/res';

import { UnavailableSendoutDrawer } from './components';
import { useJobOrderActions, useJobOrderPermissions } from './JobOrderProfileV2.hooks';
import { buildJobOrderTabs } from './JobOrderProfileV2.tabs';
import {
  buildJobOrderMenuItems,
  buildPresentationMakerJobOrderParam,
  formatJobOrderResponse
} from './JobOrderProfileV2.utils';

/**
 * Job Order profile. Composes the data, permissions and actions hooks with the shared layout.
 * Prospecting Assistant is off in V1 and is left out until it is scheduled.
 * @param {Object} props
 * @param {number|string} props.id Job order id, from the route or the quick view
 * @param {boolean} [props.enableUrlNavigation] Sync the active tab with the URL. True by default
 *   (route); the quick view passes false
 * @param {Function} [props.onEditionCompleted] Called after a profile edit in quick-view mode
 * @param {string} [props.layoutClassName] Extra className for the content area (quick view)
 * @param {Function} [props.onVerificationCompleted] Called after the Verify Urgency dialog saves
 */
export const JobOrderProfileV2 = ({
  enableUrlNavigation = true,
  id,
  layoutClassName,
  onEditionCompleted,
  onVerificationCompleted
}) => {
  const {
    catalogs,
    dataSheet,
    error,
    isAlreadyAccountable,
    isLoading,
    isOverviewLoading,
    jobOrder,
    refresh,
    reload
  } = useJobOrderProfile(id);
  const isProfileReady = !isLoading && !error && Boolean(jobOrder.id);

  const formattedJobOrder = formatJobOrderResponse({ jobOrder, dataSheet, catalogs });

  const permissions = useJobOrderPermissions({ jobOrder, isAlreadyAccountable });

  const { refreshTab, getTabVersion } = useTabRefresh();
  const {
    drawer,
    drawerItemId,
    openDrawer,
    openItemDrawer,
    closeDrawer,
    closeAndRefresh,
    closeAndRefreshTab
  } = useProfileDrawer({ onChanged: refresh, onTabChanged: refreshTab });
  const assignment = useProfileAccountability({
    canEdit: permissions.canEdit,
    entity: JOB_ORDER_PROFILE_CONFIG,
    profile: jobOrder,
    onReassign: openDrawer(JOB_ORDER_DRAWERS.Recruiters)
  });

  const actions = useJobOrderActions({
    jobOrderId: jobOrder.id,
    jobOrderTitle: jobOrder.title,
    companyId: jobOrder.company?.id,
    sendoutId: drawer === JOB_ORDER_DRAWERS.Sendout ? drawerItemId : null,
    isCompanySigned: permissions.isCompanySigned,
    openItemDrawer
  });

  const tabs = buildJobOrderTabs({
    jobOrder,
    jobOrderName: formattedJobOrder.header.title,
    dataSheet,
    catalogs,
    isOverviewLoading,
    permissions,
    sendoutDrawer: actions.sendoutDrawer,
    openDrawer,
    openItemDrawer,
    onWriteUpClick: actions.openDataSheet,
    onNewPlacementClick: actions.openNewPlacement,
    refresh,
    refreshTab
  });

  const menuItems = buildJobOrderMenuItems({
    permissions,
    canManage: assignment.canManage,
    canView: assignment.canView,
    canCollaborate: assignment.canCollaborate,
    hasAssistant: assignment.hasAssistant,
    onOpenDataSheet: actions.openDataSheet,
    openDrawer,
    onDuplicate: actions.duplicate
  });

  const handleSendoutChanged = () => {
    refresh();
    closeAndRefreshTab(JOB_ORDER_TAB_IDS.Sendouts)();
  };
  const closePlacement = () => {
    actions.clearPlacementQuery();
    closeDrawer();
  };
  const handlePlacementCompleted = () => {
    actions.clearPlacementQuery();
    closeAndRefreshTab(JOB_ORDER_TAB_IDS.Placements)();
  };

  return (
    <>
      <ProfilePageLayout
        header={formattedJobOrder.header}
        hasHeaderActions={permissions.canUsePresentationMaker}
        actions={
          <ProfileHeaderActions menuItems={menuItems}>
            <When condition={permissions.canUsePresentationMaker}>
              <FPActionButton
                icon={SvgMakeAi}
                text={strings.ai.generators.presentationsMaker.actionButton}
                onClick={openDrawer(JOB_ORDER_DRAWERS.Presentation)}
                withGradient
              />
            </When>
          </ProfileHeaderActions>
        }
        subheader={
          <>
            <ProfileSummary
              company={formattedJobOrder.summary.company}
              location={formattedJobOrder.summary.location}
              status={formattedJobOrder.status}
              created={formattedJobOrder.summary.created}
              details={formattedJobOrder.summary.details}
            />
            <ProfileAccountableBar assignment={assignment} />
          </>
        }
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        enableUrlNavigation={enableUrlNavigation}
        layoutClassName={layoutClassName}
      >
        <ProfileTabs
          tabs={tabs}
          getTabVersion={getTabVersion}
          enableUrlNavigation={enableUrlNavigation}
          organizationStorageKey={JOB_ORDER_PROFILE_CONFIG.tabsStorageKey}
          organizationSubheader={JOB_ORDER_PROFILE_CONFIG.tabsSubheader}
        />
      </ProfilePageLayout>

      <When condition={drawer === JOB_ORDER_DRAWERS.Edit}>
        <JobOrderEdit
          joborder={jobOrder}
          onEditCompleted={handleEntityEditionCompleted({
            handleEditCompleted: closeAndRefresh,
            enableUrlNavigation,
            onEditionCompleted
          })}
          onEditClosed={closeDrawer}
        />
      </When>
      <When condition={drawer === JOB_ORDER_DRAWERS.Placement}>
        <Drawer open onClose={closePlacement}>
          <PlacementCreateManager
            sendoutId={drawerItemId}
            companyId={formattedJobOrder.summary.company.id}
            jobOrderId={id}
            onClose={closePlacement}
            onCompleted={handlePlacementCompleted}
            onNewSendoutClick={openDrawer(actions.sendoutDrawer)}
          />
        </Drawer>
      </When>
      <When condition={drawer === JOB_ORDER_DRAWERS.Sendout && actions.isSendoutFormReady}>
        <Drawer open onClose={closeDrawer}>
          <SendoutsForm
            sendoutData={actions.selectedSendout}
            profile={jobOrder}
            onCreate={handleSendoutChanged}
            onEdit={handleSendoutChanged}
            onNavigate={actions.navigateFromSendout}
            onDelete={handleSendoutChanged}
            onClose={closeDrawer}
            onDuplicateSendoutSelected={openItemDrawer(JOB_ORDER_DRAWERS.Sendout)}
          />
        </Drawer>
      </When>
      <When condition={drawer === JOB_ORDER_DRAWERS.SendoutUnavailable}>
        <UnavailableSendoutDrawer
          onClose={closeDrawer}
          onGoToCompany={actions.goToCompanyProfile}
        />
      </When>
      <When condition={isProfileReady && Boolean(actions.referenceReleaseCandidateId)}>
        <ReferenceRelease
          candidateId={actions.referenceReleaseCandidateId}
          onClose={actions.closeReferenceRelease}
          onCompleted={actions.completeReferenceRelease}
        />
      </When>
      <When condition={drawer === JOB_ORDER_DRAWERS.Recruiters}>
        <ReassignRecruiterForm
          baseEndpoint={Endpoints.JobOrders}
          item={jobOrder}
          entityType={JobOrderEntity}
          onReassignCompleted={closeAndRefresh}
          onReassignClosed={closeDrawer}
        />
      </When>
      <When condition={drawer === JOB_ORDER_DRAWERS.VerifyUrgency && permissions.canVerifyUrgency}>
        <VerifyUrgencyDialog
          entityId={id}
          entityType={EntityType.Joborder}
          open
          onClose={closeDrawer}
          onSaveSucceeded={() => {
            closeDrawer();
            onVerificationCompleted?.();
          }}
        />
      </When>
      <When
        condition={drawer === JOB_ORDER_DRAWERS.Presentation && permissions.canUsePresentationMaker}
      >
        <PresentationMakerTemplate
          buildSourceParamsFn={buildPresentationMakerJobOrderParam}
          generatePresentationFn={generateJobOrderPresentation}
          leftContent={({ onSelectPresentationType, isEnabled }) => (
            <JobOrderPresentationTypeSelector
              isEnabled={isEnabled}
              jobOrder={jobOrder}
              onSelectPresentationType={onSelectPresentationType}
            />
          )}
          onClose={closeDrawer}
          sourceData={jobOrder}
        />
      </When>
      <When condition={drawer === JOB_ORDER_DRAWERS.Roleplay && permissions.canUseRoleplay}>
        <PresentationMakerTemplate
          buildSourceParamsFn={buildPresentationMakerJobOrderParam}
          generatePresentationFn={generateJobOrderPresentation}
          leftContent={({ onSelectPresentationType }) => (
            <JobOrderRoleplayScenarioSelector
              jobOrder={jobOrder}
              onSelectPresentationType={onSelectPresentationType}
            />
          )}
          onClose={closeDrawer}
          rightContentFooter={() => <CallVerbiages callType={CALL_VERBIAGES_TYPES.recruiting.id} />}
          sourceData={jobOrder}
          subtitle={strings.ai.generators.roleplay.subtitle}
          title={strings.ai.generators.roleplay.title}
        />
      </When>
      <When condition={drawer === JOB_ORDER_DRAWERS.Task}>
        <CreationTaskDrawer
          allowedTaskTypes={[TASK_TYPES.recruitJobOrder]}
          onClose={closeDrawer}
          entityData={{ id: jobOrder.id, name: jobOrder.title }}
          entityType={EntityType.Joborder}
          onTaskCreated={actions.openDailyPlan}
        />
      </When>
      <When condition={drawer === JOB_ORDER_DRAWERS.Claim && permissions.canClaimAccountability}>
        <ClaimAccountabilityDrawer
          entityId={id}
          entityType={JOB_ORDER_PROFILE_CONFIG.id}
          onClose={closeDrawer}
          onSuccess={closeAndRefresh}
        />
      </When>
    </>
  );
};
